import { DateTime } from "luxon";
import { reservationRepository } from "@/service/repository/reservationRepository";
import { copilotRepository } from "@/service/repository/copilotRepository";
import { groupItemsBySkuAndChoices, hideAllOffcanvas } from "@/utils";
import { menuRepository } from "@/service/repository/menuRepository";
import { cookieManager } from "@/service/cookieManager";
import { productOrderService } from "@/service/ProductOrderService";
import { configRepository } from "@/service/repository/configRepository";
import { useNotificationStore } from "@/stores/notificationStore";

const state = {
	viewsCopilot: {
		kitchen: "Cocina",
		bar: "Barra",
		barManager: "TPV",
		intern: "Pasante",
	},

	notificationsType: {
		SECOND_DRINK: "upselling_drinks_reminder",
	},

	selectedDate: DateTime.now().toISODate(),
	serverLogicalDate: null,
	latestManualDateOverride: null,
	// When true, user is viewing a date different from today and automatic date updates are blocked
	isViewingNonTodayDate: false,
	availableRoles: [],
	selectedRoleId: "0",
	reservations: [],
	notifications: [],
	accounts: [],
	assistantNotifications: [],
	isPlayNotificationSound: false,
	isLoadingGetReservations: false,
	isLoadingVenueMenus: false,
	isShowToastScanQr: null,
	isShowModalScanQr: null,
	toast: {
		showToast: false,
		toastMessage: "",
		idToast: "",
	},
	newNotification: null,
	commanderReservation: null,
	notificationMode: "0",
	pendingNotifications: [],
	tables: [],
	reservationId: null,
	serviceEvents: [],
	selectedProductOrdersToServe: [],
	selectedProductOrdersToPrepared: [],
	selectedProductOrdersToClient: [],
	productOrder: null,
	selectedReservationId: null,

	isRemoveProduct: false,
	localProductIdModalReject: null,

	reservationUserUid: null,
	showAllProductOrdersInKitchen: true,
	selectedStations: [],

	config: {
		zoneFilter: false,
	},
	selectedZoneFilter: [],

	promotionsEnabled: false,
	customerReadyToOrder: true,
	isNotificationCustomerReadyToOrder: {
		show: false,
		id: null,
	},

	typeServicesSearch: [],
	selectedProductOrderToModifyPrice: null,
};

const getters = {
	promotionsEnabled: (state) => {
		return state.promotionsEnabled;
	},
	customerReadyToOrder: (state) => {
		return state.customerReadyToOrder;
	},
	productOrder: (state) => {
		return state.productOrder;
	},
	isRemoveProduct: (state) => {
		return state.isRemoveProduct;
	},
	selectedReservationId: (state) => {
		return state.selectedReservationId;
	},
	selectedReservation: (state) => {
		return state.reservations.find((reservation) => reservation.id === state.selectedReservationId);
	},

	selectedProductOrdersToServe: (state) => {
		return state.selectedProductOrdersToServe;
	},
	selectedProductOrdersToPrepared: (state) => {
		return state.selectedProductOrdersToPrepared;
	},
	selectedProductOrderToModifyPrice: (state) => {
		return state.selectedProductOrderToModifyPrice;
	},
	selectedProductOrdersToClient: (state, getters) => {
		const readyProductOrdersIds =
			getters.serviceEventPendingService
				.flatMap((serviceEvent) => serviceEvent.items)
				?.filter((productOrder) => productOrder.statusProductOrder < 100 && productOrder.requirementProductCopilotRoles?.some((role) => role.name === getters.getSelectedRole?.name))
				?.map((productOrder) => productOrder.productOrderId) || [];

		return state.selectedProductOrdersToClient.filter((productOrderId) => readyProductOrdersIds.includes(productOrderId));
	},
	hotTable: (state) => {
		const STATUS_IN_HOT_TABLE = 7;
		let selectedRole = getters.getSelectedRole(state);
		return state.reservations
			.flatMap((reservation) => (reservation.cartSession ? reservation.cartSession.productOrders : []))
			.filter((order) => order)
			.flatMap((order) => {
				return order.items
					.filter((item) => {
						return !productOrderService.checkIsGhostProductOrder(item) && item.statusProductOrder === STATUS_IN_HOT_TABLE && item.requirementProductCopilotRoles?.some((role) => role.name === selectedRole.copilotView.name);
					})
					.map((item) => ({
						...item,
						createdDateTime: item.dateCreated || order.createdDateTime,
					}));
			})
			.sort((a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime));
	},
	selectedDate: (state) => {
		return state.selectedDate;
	},
	latestManualDateOverride: (state) => {
		return state.latestManualDateOverride;
	},
	isViewingNonTodayDate: (state) => {
		return state.isViewingNonTodayDate;
	},
	serverLogicalDate: (state) => {
		return state.serverLogicalDate;
	},
	showTicketRequestNotifications: (state, getters) => {
		// Assuming isKitchenSelectedRole is also a getter
		return !getters.isKitchenSelectedRole; // "Esto no lo tengo tan seguro porque el del TPV imprime el ticket de una cuenta ->" || !selectedRoleObject?.name.includes("TPV + Barra");
	},
	getSelectedRole: (state) => {
		if (!state || !state.availableRoles || state.availableRoles.length === 0) {
			return null;
		}
		return state.availableRoles.find((role) => role.id === state.selectedRoleId);
	},
	isKitchenSelectedRole: (state) => {
		const kitchenRoleObject = state.availableRoles.find((role) => role.name === "Cocina");
		const kitchenRole = kitchenRoleObject ? kitchenRoleObject.id : null;
		return state.selectedRoleId === kitchenRole;
	},
	isOnlyRoom: (state) => {
		const onlyRoomRoleObject = state.availableRoles.find((role) => role.name === "1 en Sala");
		const onlyRole = onlyRoomRoleObject ? onlyRoomRoleObject.id : null;
		return state.selectedRoleId === onlyRole;
	},
	pendingOrders: (state, getters) => {
		const selectedZones = Array.isArray(state.selectedZoneFilter) ? state.selectedZoneFilter.filter((z) => typeof z === "string") : [];

		const statusCartSessionRoleTypes = getters.isKitchenSelectedRole ? [2] : [1, 3];

		const orders = state.reservations
			.filter((reservation) => {
				const zone = reservation.roomName;
				// Si no hay filtro → incluir todas. Si hay filtro → incluir solo si está la zona.
				return selectedZones.length === 0 || selectedZones.includes(zone);
			})
			.flatMap((reservation) => {
				if (!reservation.cartSession) return [];

				// Añadimos la zona a cada orden para que sea accesible después
				return reservation.cartSession.productOrders.map((order) => ({
					...order,
					reservationZone: reservation.roomName,
				}));
			})
			.filter((order) => order && statusCartSessionRoleTypes.includes(order.cartSessionStatus))
			.map((order) => {
				if (order.cartSessionStatus > 1) {
					return {
						...order,
						items: order.items.filter((item) => item.type === "Comida"),
					};
				}
				return order;
			})
			.sort((a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime));

		// Agrupa los items por SKU y choices (esto puede usar la zona si lo necesitas luego)
		const groupedOrders = orders.map((order) => ({
			...order,
			items: groupItemsBySkuAndChoices([order]),
		}));

		return groupedOrders;
	},

	allOrderProducts(state) {
		return state.reservations
			.flatMap((reservation) => (reservation.cartSession ? reservation.cartSession.productOrders : []))
			.filter((order) => order)
			.flatMap((order) => {
				return order.items.map((item) => ({
					...item,
					createdDateTime: item.dateCreated || order.createdDateTime,
				}));
			})
			.sort((a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime));
	},
	availableRoles: (state) => {
		return state.availableRoles;
	},
	selectedRole: (state) => {
		return state.selectedRoleId;
	},
	reservations: (state) => {
		return state.reservations.slice().sort((a, b) => {
			return new Date(a.startDateTime) - new Date(b.startDateTime);
		});
	},
	notifications: (state, getters) => {
		if (!getters.zoneFilter) {
			return state.notifications;
		}

		const selectedZones = Array.isArray(state.selectedZoneFilter) ? state.selectedZoneFilter.filter((z) => typeof z === "string") : [];

		if (selectedZones.length === 0) {
			return state.notifications;
		}

		const normalizedZones = selectedZones.map((z) => z.toLowerCase().trim());

		return state.notifications.filter((notification) => {
			const roomName = notification.reservation?.roomName?.toLowerCase().trim();
			return roomName && normalizedZones.includes(roomName);
		});
	},

	accounts: (state) => {
		return state.accounts;
	},
	assistantNotifications: (state, getters) => {
		if (!getters.zoneFilter) {
			return state.assistantNotifications;
		}

		const selectedZones = Array.isArray(state.selectedZoneFilter) ? state.selectedZoneFilter.filter((z) => typeof z === "string") : [];

		if (selectedZones.length === 0) {
			return state.assistantNotifications;
		}

		const normalizedZones = selectedZones.map((z) => z.toLowerCase().trim());

		return state.assistantNotifications.filter((notification) => {
			const roomNames = Array.isArray(notification.rooms) ? notification.rooms.map((room) => room.name?.toLowerCase().trim()).filter(Boolean) : [];

			return roomNames.some((roomName) => normalizedZones.includes(roomName));
		});
	},

	isPlayNotificationSound: (state) => {
		return state.isPlayNotificationSound;
	},
	isLoadingGetReservations: (state) => {
		return state.isLoadingGetReservations;
	},
	isLoadingVenueMenus: (state) => {
		return state.isLoadingVenueMenus;
	},
	isShowToastScanQr: (state) => {
		return state.isShowToastScanQr;
	},
	toast: (state) => {
		return state.toast;
	},
	newNotification: (state) => {
		return state.newNotification;
	},
	menusVenue: (state) => {
		return state.venueMenus;
	},
	commanderReservation: (state) => {
		return state.commanderReservation;
	},
	notificationMode: (state) => {
		return state.notificationMode;
	},
	pendingNotifications: (state) => {
		return state.pendingNotifications;
	},
	tables: (state) => {
		return state.tables;
	},
	reservationId(state) {
		return state.reservationId;
	},
	reservationUserUid(state) {
		return state.reservationUserUid;
	},
	serviceEvents: (state) => {
		return state.serviceEvents;
	},
	serviceEventPendingService(state) {
		const selectedZones = Array.isArray(state.selectedZoneFilter) ? state.selectedZoneFilter.filter((z) => typeof z === "string") : [];

		return state.serviceEvents
			.filter((serviceEvent) => serviceEvent.serviceEventStatus !== 100)
			.map((serviceEvent) => {
				const selectedRoleObject = state.availableRoles.find((role) => role.id === state.selectedRoleId);

				const servesDrinks = selectedRoleObject?.notificationTypes?.some((nt) => nt.name === "order_ready_drinks") || false;
				const servesFood = selectedRoleObject?.notificationTypes?.some((nt) => nt.name === "order_ready_food") || false;

				const itemsFiltered = serviceEvent.items.filter((item) => {
					const isRoleMatch = selectedRoleObject?.copilotView?.name.includes("Cocina") || selectedRoleObject?.copilotView?.name.includes("Barra") || item.requirementProductCopilotRoles.some((role) => (servesFood && role.name === "Cocina") || (servesDrinks && role.name === "Barra"));

					const itemZone = item.reservationInfo?.zone;
					const isZoneMatch = selectedZones.length === 0 || (itemZone && selectedZones.includes(itemZone));

					const isStatusValid = item.statusProductOrder !== 100;

					return isRoleMatch && isZoneMatch && isStatusValid;
				});

				return {
					...serviceEvent,
					items: itemsFiltered,
				};
			})
			.filter((serviceEvent) => serviceEvent.items.length > 0);
	},

	view(state) {
		const role = state.availableRoles.find((role) => role.id === state.selectedRoleId);
		hideAllOffcanvas();
		return role && role.copilotView && role.copilotView.name ? role.copilotView.name : null;
	},
	copilotViewRolName(state) {
		const role = state.availableRoles.find((role) => role.id === state.selectedRoleId);
		return role && role.copilotView && role.name ? role.name : role.copilotView.name;
	},
	elaborationStations(state) {
		const role = getters.getSelectedRole(state);
		return role && role.elaborationStations ? role.elaborationStations : [];
	},
	reservationsWithComments(state) {
		return state.reservations.map((reservation) => {
			const cartSessionOrders = (reservation.cartSession?.productOrders || []).map((cartSessionOrder) => {
				const items = cartSessionOrder.items.map((item) => item.name).join(", ");
				const isAllProductsServed = cartSessionOrder.items.every((item) => item.statusProductOrder === 100);
				const createdDateTime = cartSessionOrder.createdDateTime;

				// Recoger comentarios de los productos y el mensaje general del pedido
				// Solo incluimos comentarios de productos que son "Menú" en la cabecera para evitar duplicados en productos simples
				const itemComments = (cartSessionOrder.items || [])
					.filter((item) => item.staffComments && (item.isMenu === "SI" || item.isMenu === true))
					.map((item) => {
						if (Array.isArray(item.staffComments)) {
							return item.staffComments.map((c) => (typeof c === "string" ? c : c.comment)).join(", ");
						}
						return item.staffComments;
					});

				const allMessages = [cartSessionOrder.clientMessage, ...itemComments].filter((message) => message && typeof message === "string" && message.trim() !== "");

				const clientMessage = allMessages.length ? [...new Set(allMessages)].join(", ") : null;

				return { createdDateTime, isAllProductsServed, items, clientMessage };
			});
			return { reservationId: reservation.id, table: reservation.table, cartSessionOrders };
		});
	},
	localProductIdModalReject: (state) => {
		return state.localProductIdModalReject;
	},
	showAllProductOrdersInKitchen(state) {
		return state.showAllProductOrdersInKitchen;
	},
	selectedStations(state) {
		return state.selectedStations;
	},

	zoneReservation(state) {
		return [...new Set(state.tables.map((table) => table.room))];
	},

	zoneFilter(state) {
		return state.config?.zoneFilter;
	},
	selectedZoneFilter(state) {
		return state.selectedZoneFilter;
	},

	pendingProductsByRole(state, getters) {
		const selectedRole = getters.getSelectedRole;
		if (!selectedRole) return [];

		const targetRoleName = selectedRole.copilotView?.name;
		if (!targetRoleName) return [];

		// Función auxiliar para crear nombres descriptivos con opciones
		const createDisplayName = (productName, choices) => {
			if (!choices || choices.length === 0) {
				return productName;
			}

			// Crear un nombre descriptivo que incluya las opciones importantes
			const optionsText = choices
				.map((choice) => {
					// Usar las propiedades correctas según la estructura real
					return choice.choiceName;
				})
				.join(", ");

			return `${productName} (${optionsText})`;
		};

		// Obtener todos los productos pendientes de todas las reservas
		const allPendingProducts = state.reservations
			.filter((reservation) => reservation.cartSession && reservation.cartSession.productOrders)
			.flatMap((reservation) =>
				reservation.cartSession.productOrders
					.filter((order) => order.items && order.items.length > 0)
					.flatMap((order) =>
						order.items
							.filter(
								(item) =>
									// Solo productos pendientes (status < 100 y no completados)
									item.statusProductOrder < 100 &&
									item.statusProductOrder > 0 &&
									item.statusProductOrder !== 3 &&
									// Filtrar por rol seleccionado
									item.requirementProductCopilotRoles?.some((role) => role.name === targetRoleName),
							)
							.map((item) => ({
								...item,
								reservationId: reservation.id,
								table: reservation.table,
								roomName: reservation.roomName,
							})),
					),
			);

		// Agrupar productos por nombre y opciones
		const groupedProducts = {};

		allPendingProducts.forEach((product) => {
			// Debug: ver qué propiedades tiene el producto
			console.log("Producto completo:", product);
			console.log("Propiedades del producto:", Object.keys(product));

			// Buscar las opciones en diferentes propiedades posibles
			let choices = product.choices || product.options || product.productOptions || product.modifiers || [];
			console.log("Opciones encontradas:", choices);

			// Crear clave única que incluya SKU y opciones específicas
			let choicesKey = "sin-opciones";

			if (choices && choices.length > 0) {
				// Ordenar las opciones para asegurar consistencia en la agrupación
				const sortedChoices = choices
					.map((choice) => {
						// Usar las propiedades correctas según la estructura real
						const name = choice.typeName || choice.choiceName || choice.type || choice.name || "opcion";
						const value = choice.choice || choice.choiceName || choice.value || choice.text || "valor";
						return `${name}:${value}`;
					})
					.sort();
				choicesKey = sortedChoices.join("|");
			}

			const productKey = `${product.productSku}_${choicesKey}`;

			if (!groupedProducts[productKey]) {
				groupedProducts[productKey] = {
					productName: product.name,
					productSku: product.productSku,
					choices: choices,
					totalUnits: 0,
					reservations: [],
					statusCounts: {},
					// Crear un nombre descriptivo que incluya las opciones
					displayName: createDisplayName(product.name, choices),
				};
			}

			// Sumar unidades
			const units = product.units || 1;
			groupedProducts[productKey].totalUnits += units;

			// Contar por estado
			const status = product.statusProductOrder;
			if (!groupedProducts[productKey].statusCounts[status]) {
				groupedProducts[productKey].statusCounts[status] = 0;
			}
			groupedProducts[productKey].statusCounts[status] += units;

			// Agregar información de reserva
			groupedProducts[productKey].reservations.push({
				reservationId: product.reservationId,
				table: product.table,
				roomName: product.roomName,
				units: units,
				status: status,
			});
		});

		// Convertir a array y ordenar por cantidad total
		return Object.values(groupedProducts)
			.sort((a, b) => b.totalUnits - a.totalUnits)
			.map((product) => ({
				...product,
				displayUnits: `${product.totalUnits}`,
				displayText: `${product.displayName}`,
				// Agregar información de estado para mostrar prioridades
				hasUrgentStatus: product.statusCounts[2] > 0, // status 2 es urgente
				statusSummary: Object.entries(product.statusCounts)
					.map(([status, count]) => `${count} (estado ${status})`)
					.join(", "),
			}));
	},

	reservationsWithProducts: (state) => {
		return state.reservations
			.filter((reservation) => reservation.status > 0)
			.map((reservation) => ({
				...reservation,
				orderedItems: reservation.cartSession
					? reservation.cartSession.productOrders.flatMap(
						(order) =>
							order.items.map((item) => ({
								...item,
								createdDateTime: item.dateCreated || order.createdDateTime,
								table: reservation.table,
							})),
						// eslint-disable-next-line no-mixed-spaces-and-tabs
					)
					: [],
			}))
			.sort((a, b) => {
				const minDateA = a.orderedItems
					.filter((item) => item.statusProductOrder < 100)
					.map((item) => new Date(item.createdDateTime.replace(" ", "T")))
					.reduce((min, current) => (current < min ? current : min), new Date(8640000000000000)); // Máxima fecha posible

				const minDateB = b.orderedItems
					.filter((item) => item.statusProductOrder < 100)
					.map((item) => new Date(item.createdDateTime.replace(" ", "T")))
					.reduce((min, current) => (current < min ? current : min), new Date(8640000000000000)); // Máxima fecha posible

				return minDateA - minDateB;
			});
	},

	baseReservationsWithCartAndProducts: (state, getters, rootState, rootGetters) => {
		const externalIsGhost = rootGetters["copilot/isGhostProductOrder"] || ((item) => item?.isGhost === true);

		const showGhostProductOrders = rootGetters["preparationArea/showGhostProductOrders"] ?? true;

		const areAllProductOrdersCompleted = (reservation) => {
			if (!reservation.cartSession?.productOrders) return false;
			return reservation.cartSession.productOrders.every((order) => {
				// const orderDone = order.cartSessionStatus === 2 || order.cartSessionStatus === 100
				const items = order.items || [];
				const hasItems = items.length > 0;
				const itemsDone = hasItems && items.every((it) => it.statusProductOrder === 100 || (it.statusProductOrder >= 3 && it.statusProductOrder < 7) || it.statusProductOrder <= -1 || it.statusProductOrder === -3);

				return /*orderDone && */ itemsDone;
			});
		};

		return getters.reservationsWithProducts.filter((reservation) => {
			const menuEnabled = reservation.menuAccessEnabled === 1;
			const hasCart = !!reservation.cartSession;
			const notCompleted = !areAllProductOrdersCompleted(reservation);
			if (!menuEnabled || !hasCart || !notCompleted) return false;

			if (!showGhostProductOrders) {
				const allProducts = reservation.cartSession.productOrders.flatMap((po) => po.items || []);
				const hasVisibleProducts = allProducts.some((p) => !externalIsGhost(p));
				if (!hasVisibleProducts) return false;
			}

			return true;
		});
	},

	onlyBeveragesFromDishClass: (state, getters, rootState, rootGetters) => {
		const selectedRole = rootGetters["copilot/getSelectedRole"];
		const myRoleName = selectedRole?.copilotView?.name;
		if (!myRoleName) return [];

		const target = myRoleName.toLowerCase();

		const hasItemForMyRole = (item) => (item?.requirementProductCopilotRoles || []).some((role) => (role?.name || "").toLowerCase() === target);

		return (getters.baseReservationsWithCartAndProducts || []).filter((reservation) => {
			const items = reservation?.cartSession?.productOrders ? reservation.cartSession.productOrders.flatMap((po) => po.items || []) : [];

			if (items.length === 0) return false;
			return !items.some(hasItemForMyRole);
		});
	},

	amountPaxOnlyBeveragesFromDishClass: (state, getters) => {
		return getters.onlyBeveragesFromDishClass.reduce((t, r) => t + (r.pax || 0), 0);
	},

	sortedReservationsNotCartSession: (state, getters) => {
		return (getters.reservationsWithProducts || []).filter((reservation) => reservation.status !== 10 && reservation.menuAccessEnabled === 1 && !reservation.cartSession);
	},

	amountPaxReservationsNotCartSession: (state, getters) => {
		return (getters.sortedReservationsNotCartSession || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	sortedReservations: (state, getters) => {
		const areAllProductOrdersCompleted = (reservation) => {
			if (!reservation.cartSession?.productOrders) return false;

			return reservation.cartSession.productOrders.every((order) => {
				const orderDone = order.cartSessionStatus === 2; /*|| order.cartSessionStatus === 100*/
				const items = order.items || [];
				const hasItems = items.length > 0;
				const itemsDone = hasItems && items.every((it) => it.statusProductOrder === 100 || it.statusProductOrder >= 3 || it.statusProductOrder <= -1);
				return orderDone && itemsDone;
			});
		};

		return (getters.reservationsWithProducts || []).filter((reservation) => reservation.menuAccessEnabled !== 1 && !areAllProductOrdersCompleted(reservation));
	},
	reservationsToCome: (state, getters) => {
		return (getters.reservations || []).filter((reservation) => !reservation.finishedDateTime && reservation.menuAccessEnabled === 0 && reservation.status == 1);
	},

	amountPaxSortedReservations: (state, getters) => {
		return (getters.sortedReservations || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	finalizedReservations: (state, getters) => {
		return (getters.reservationsWithProducts || []).filter((reservation) => reservation.finishedDateTime !== null);
	},

	amountPaxFinalizedReservations: (state, getters) => {
		return (getters.finalizedReservations || []).reduce((total, r) => total + (r.pax || 0), 0);
	},

	countFinalizedReservations: (state, getters) => {
		return (getters.finalizedReservations || []).length;
	},

	amountPaxTotalReservations: (state, getters) => {
		return (getters.reservationsToCome || []).reduce((total, r) => total + (r.pax || 0), 0);
	},
	countTotalReservations: (state, getters) => {
		return getters.reservationsToCome || [];
	},
	isNotificationCustomerReadyToOrder: (state) => {
		return state.isNotificationCustomerReadyToOrder;
	},

	accountsActive: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters["venue/copilotConfig"];
		const view = rootGetters["copilot/view"];

		let list = state.reservations.filter((reservation) => reservation.menuAccessEnabled === 1 && reservation.status > 0 && reservation.finishedDateTime === null && reservation.status !== 10);

		if (view !== "TPV") {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== "barra");
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},
	accountsFinished: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters["venue/copilotConfig"];
		const view = rootGetters["copilot/view"];

		let list = state.reservations.filter((r) => r.menuAccessEnabled === 1 && r.finishedDateTime?.date);

		if (view !== "TPV" && !showBar.weCopilotShowBar) {
			list = list.filter((r) => r.roomName?.toLowerCase() !== "barra");
		}

		return list.sort((a, b) => {
			const dateA = new Date(a.finishedDateTime.date);
			const dateB = new Date(b.finishedDateTime.date);

			return dateB - dateA;
		});
	},

	accountsNoShow: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters["venue/copilotConfig"];
		const view = rootGetters["copilot/view"];

		let list = state.reservations.filter((reservation) => reservation.status === -4);

		if (view !== "TPV") {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== "barra");
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},

	accountsReservation: (state, getters, rootState, rootGetters) => {
		const showBar = rootGetters["venue/copilotConfig"];
		const view = rootGetters["copilot/view"];

		let list = state.reservations.filter((reservation) => reservation.menuAccessEnabled === 0 && reservation.status > 0 && reservation.finishedDateTime === null && reservation.status !== 10);

		if (view !== "TPV") {
			if (!showBar.weCopilotShowBar) {
				list = list.filter((r) => r.roomName?.toLowerCase() !== "barra");
			}
		}

		return list.sort((a, b) => {
			const isAAccountPayment = a.status === 7;
			const isBAccountPayment = b.status === 7;

			const isAStarted = a.menuAccessEnabled === 1;
			const isBStarted = b.menuAccessEnabled === 1;

			if (isAAccountPayment && !isBAccountPayment) return -1;
			if (!isAAccountPayment && isBAccountPayment) return 1;

			if (isAStarted && !isBStarted) return -1;
			if (!isAStarted && isBStarted) return 1;

			return 0;
		});
	},

	typeServicesSearch: (state) => {
		return state.typeServicesSearch;
	},
};

const mutations = {
	setProductOrder(state, payload) {
		state.productOrder = payload.productOrder;
	},
	setSelectedReservationId(state, payload) {
		state.selectedReservationId = payload.reservationId;
	},
	setSelectedReservationUserId(state, payload) {
		state.reservationUserUid = payload;
	},
	setIsRemoveProduct(state, value) {
		state.isRemoveProduct = value;
	},
	setTypeServicesSearch(state, value) {
		state.typeServicesSearch = value;
	},
	setSelectedProductOrderToModifyPrice(state, value) {
		state.selectedProductOrderToModifyPrice = value;
	},
	setSelectedDate(state, payload) {
		const payloadDate = payload.selectedDate;
		let newDate;
		if (typeof payloadDate === "string") {
			// Parse ISO 8601 string
			newDate = DateTime.fromISO(payloadDate).toISODate();
		} else if (payloadDate instanceof Date) {
			// Convert JavaScript Date object
			newDate = DateTime.fromJSDate(payloadDate).toISODate();
		} else {
			throw new Error("Invalid date format. Expected a string or Date object.");
		}
		state.selectedDate = newDate;

		// Automatically determine if we're viewing a non-today date
		// Compare with serverLogicalDate if available, otherwise with today's date
		const referenceDate = state.serverLogicalDate || DateTime.now().toISODate();
		const normalizedReference = String(referenceDate).slice(0, 10);
		const normalizedNew = String(newDate).slice(0, 10);

		state.isViewingNonTodayDate = normalizedNew !== normalizedReference;
	},
	setLatestManualDateOverride(state, payload) {
		state.latestManualDateOverride = payload.timestamp;
	},
	setIsViewingNonTodayDate(state, payload) {
		state.isViewingNonTodayDate = payload;
	},
	setServerLogicalDate(state, date) {
		state.serverLogicalDate = date;
	},
	setAvailableRoles(state, payload) {
		state.availableRoles = payload.availableRoles;
	},
	setSelectedRoleId(state, payload) {
		let finalId = payload.selectedRoleId;
		const roleExists = state.availableRoles.some((r) => r.id === finalId);

		if (!roleExists) {
			const sala = state.availableRoles.find((r) => r.name === "Sala");
			finalId = sala ? sala.id : null;
		}

		if (finalId) {
			cookieManager.setCookie("selectedRole", finalId);
		}

		state.selectedRoleId = finalId;

		state.showAllProductOrdersInKitchen = true;
		state.selectedStations = [];
		state.selectedReservationId = null;
	},
	setSelectedLocale(state, payload) {
		cookieManager.setCookie("selectedLocale", payload.selectedLocale);
		state.selectedLocale = payload.selectedLocale;
	},
	setReservations(state, payload) {
		state.reservations = payload.reservations;
		state.isLoadingGetReservations = false;
	},
	setServiceEvents(state, payload) {
		state.serviceEvents = payload.serviceEvents;
	},
	setNotifications(state, payload) {
		if (state.notifications.length === 0 && payload.notifications.length > 0) {
			state.isPlayNotificationSound = true;
		}
		state.notifications = payload.notifications;
	},
	setIsPlayNotificationSound(state, payload) {
		state.isPlayNotificationSound = payload.isPlayNotificationSound;
	},
	setIsLoadingGetReservations(state, payload) {
		state.isLoadingGetReservations = payload.isLoadingGetReservations;
	},
	setIsLoadingVenueMenus(state, payload) {
		state.isLoadingVenueMenus = payload.isLoadingVenueMenus;
	},
	setAccounts(state, payload) {
		if (state.accounts.length === 0 && payload.accounts.length > 0) {
			state.isPlayNotificationSound = true;
		}
		state.accounts = payload.accounts;
	},
	setAssistantNotifications(state, payload) {
		state.assistantNotifications = payload.assistantNotifications;
	},
	setIsShowToastScanQr(state, payload) {
		state.isShowToastScanQr = payload.isShowToastScanQr;
	},
	setToast(state, payload) {
		state.toast = payload.toast;
	},
	setNewNotification(state, payload) {
		state.isPlayNotificationSound = true;
		state.newNotification = payload.newNotification;
	},
	setVenueMenus(state, payload) {
		state.venueMenus = payload.venueMenus;
	},
	setCommanderReservation(state, payload) {
		state.commanderReservation = payload.commanderReservation;
	},
	setNotificationMode(state, payload) {
		state.notificationMode = payload.notificationMode;
	},
	addNotificationToPendingNotifications(state, payload) {
		state.pendingNotifications.push(payload.notification);
	},
	deleteNotificationToPendingNotifications(state, payload) {
		payload.notificationId ? (state.pendingNotifications = state.pendingNotifications.filter((notification) => notification.id !== payload.notificationId)) : (state.pendingNotifications = []);
	},
	setTables(state, payload) {
		state.tables = payload.tables;
	},
	setReservationId(state, id) {
		state.reservationId = id;
	},
	setSelectedProductOrdersToServe(state, payload) {
		state.selectedProductOrdersToServe = payload.selectedProductOrdersToServe;
	},
	setSelectedProductOrdersToPrepared(state, payload) {
		state.selectedProductOrdersToPrepared = payload.selectedProductOrdersToPrepared;
	},
	setSelectedProductOrdersToClient(state, payload) {
		state.selectedProductOrdersToClient = payload.selectedProductOrdersToClient();
	},
	updateProductOrdersStatus(state, payload) {
		state.reservations = state.reservations.map((reservation) => {
			if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
				return reservation;
			}

			return {
				...reservation,
				cartSession: {
					...reservation.cartSession,
					productOrders: reservation.cartSession.productOrders.map((productOrder) => {
						if (!Array.isArray(productOrder.items)) {
							return productOrder;
						}

						return {
							...productOrder,
							items: productOrder.items.map((item) => {
								if (payload.productOrdersIdsToUpdate.includes(item.productOrderId)) {
									const updatedItem = {
										...item,
										statusProductOrder: payload.status,
									};

									// Si el estado cambia a 2 (preparando), actualizar datePreparing
									if (payload.status === 2 && !item.datePreparing) {
										updatedItem.datePreparing = {
											date: new Date().toISOString(),
										};
									}

									return updatedItem;
								}
								return item;
							}),
						};
					}),
				},
			};
		});
	},
	updateSingleProductOrder(state, payload) {
		// const selectedRoleObject = state.availableRoles.find(role => role.id === state.selectedRole);
		// const selectedRoleName = selectedRoleObject ? selectedRoleObject.name : null;

		state.reservations = state.reservations.map((reservation) => {
			if (!reservation.cartSession || !Array.isArray(reservation.cartSession.productOrders)) {
				return reservation;
			}

			return {
				...reservation,
				cartSession: {
					...reservation.cartSession,
					productOrders: reservation.cartSession.productOrders.map((productOrder) => {
						if (!Array.isArray(productOrder.items)) {
							return productOrder;
						}

						return {
							...productOrder,
							items: productOrder.items.map((item) => {
								if (payload.productOrderIdToUpdate === item.productOrderId) {
									const updatedItem = {
										...item,
										statusProductOrder: payload.status,
									};

									// Si el estado cambia a 2 (preparando), actualizar datePreparing
									if (payload.status === 2 && !item.datePreparing) {
										updatedItem.datePreparing = {
											date: new Date().toISOString(),
										};
									}

									return updatedItem;
								}
								return item;
							}),
						};
					}),
				},
			};
		});

		state.serviceEvents = state.serviceEvents.map((serviceEvent) => {
			const items = serviceEvent.items.map((item) => {
				if (payload.productOrderIdToUpdate === item.productOrderId) {
					const updatedItem = {
						...item,
						statusProductOrder: payload.status,
					};

					// Si el estado cambia a 2 (preparando), actualizar datePreparing
					if (payload.status === 2 && !item.datePreparing) {
						updatedItem.datePreparing = {
							date: new Date().toISOString(),
						};
					}

					return updatedItem;
				}
				return item;
			});
			return { ...serviceEvent, items };
		});
	},
	addServiceEvent(state, payload) {
		state.serviceEvents.push(payload.serviceEvent);
	},
	updateReservation(state, payload) {
		state.reservations = state.reservations.map((reservation) => {
			return reservation.id === payload.updatedReservation.id ? payload.updatedReservation : reservation;
		});
	},
	updateServiceEvent(state, payload) {
		state.serviceEvents = state.serviceEvents.map((serviceEvent) => {
			return serviceEvent.serviceEventId === payload.serviceEvent.serviceEventId ? payload.serviceEvent : serviceEvent;
		});
	},
	addReservationToReservations(state, payload) {
		const logicalDayStartHour = 6;
		const logicalDayStart = DateTime.fromFormat(state.selectedDate, "yyyy-MM-dd").plus({ hours: logicalDayStartHour });
		const reservationTime = DateTime.fromFormat(payload.reservation.startDateTime, "yyyy-MM-dd HH:mm:ss");
		const selectedDayStart = logicalDayStart.minus({ days: reservationTime.hour < logicalDayStartHour ? 1 : 0 });
		const selectedDayEnd = logicalDayStart.plus({ days: 1 });

		const isSameLogicalDay = reservationTime >= selectedDayStart && reservationTime < selectedDayEnd;

		if (isSameLogicalDay) {
			state.reservations.push(payload.reservation);
		}
	},
	addFictitiousAccount(state, fictitiousAccount) {
		state.reservations.push(fictitiousAccount);
	},
	removeFictitiousAccount(state, fictitiousAccountId) {
		const index = state.reservations.findIndex((reservation) => reservation.id === fictitiousAccountId);
		if (index !== -1) {
			state.reservations.splice(index, 1);
		}
	},
	setLocalProductIdModalReject(state, product) {
		if (!product || !product.reservationId || !product.orderId) {
			state.localProductIdModalReject = null;
			return;
		}

		const reservation = state.reservations.find((reservation) => reservation.id === product.reservationId);

		if (!reservation) {
			state.localProductIdModalReject = null;
			return;
		}

		if (reservation.cartSession) {
			for (let order of reservation.cartSession.productOrders) {
				const item = order.items.find((item) => item.productOrderId === product.orderId);

				if (item) {
					state.localProductIdModalReject = item; // Asigna directamente el producto encontrado
					return;
				}
			}
			state.localProductIdModalReject = null; // Asigna null si no se encuentra el producto
		} else {
			state.localProductIdModalReject = null;
		}
	},
	toggleProductOrderSelection(state, productOrderId) {
		const index = state.selectedProductOrdersToServe.indexOf(productOrderId);
		if (index !== -1) {
			// Deselect the product if it is already selected
			state.selectedProductOrdersToServe.splice(index, 1);
		} else {
			// Select the product if it is not yet selected
			state.selectedProductOrdersToServe.push(productOrderId);
		}
	},
	clearSelectedProductOrdersToServe(state) {
		state.selectedProductOrdersToServe = [];
	},
	toggleProductOrderToClientSelection(state, productOrderId) {
		const index = state.selectedProductOrdersToClient.indexOf(productOrderId);
		if (index !== -1) {
			// Deselect the product if it is already selected
			state.selectedProductOrdersToClient.splice(index, 1);
		} else {
			// Select the product if it is not yet selected
			state.selectedProductOrdersToClient.push(productOrderId);
		}
	},
	clearSelectedProductOrdersToClient(state) {
		state.selectedProductOrdersToClient = [];
	},
	deleteProductOrderFromSelectedProductOrdersToServe(state, payload) {
		state.selectedProductOrdersToServe = state.selectedProductOrdersToServe.filter((productOrderId) => productOrderId !== payload.productOrderId);
	},
	setShowAllProductOrdersInKitchen(state, payload) {
		state.showAllProductOrdersInKitchen = payload.show;
	},
	setSelectedStations(state, payload) {
		state.selectedStations = payload.selectedStations;
	},
	updateReservationsAfterMerging(state, payload) {
		state.reservations = state.reservations.map((reservation) => (reservation.id === payload.reservationToUpdate.id ? payload.reservationToUpdate : reservation)).filter((reservation) => reservation.id !== payload.mergedReservationId);
	},
	reassignProductOrdersReservation(state, payload) {
		const { reassignedProductOrderIds, reservationUpdated } = payload;

		state.reservations = state.reservations
			.map((reservation) => {
				if (reservation.cartSession && Array.isArray(reservation.cartSession.productOrders)) {
					reservation.cartSession.productOrders = reservation.cartSession.productOrders
						.map((productOrder) => {
							productOrder.items = productOrder.items.filter((item) => !reassignedProductOrderIds.includes(item.productOrderId));
							return productOrder;
						})
						.filter((productOrder) => productOrder.items.length > 0);
				}
				return reservation;
			})
			.map((reservation) => {
				return reservationUpdated.id === reservation.id ? reservationUpdated : reservation;
			});
	},

	SET_CONFIG(state, config) {
		state.config = config;
	},
	setSelectedZoneFilter(state, payload) {
		if (payload === null || payload === undefined) {
			state.selectedZoneFilter = [];
			localStorage.setItem("selectedZoneFilter", JSON.stringify([]));
			return;
		}
		const zones = Array.isArray(payload) ? payload.filter((z) => typeof z === "string" && z.trim() !== "") : [];
		state.selectedZoneFilter = zones;
		localStorage.setItem("selectedZoneFilter", JSON.stringify(zones));
	},
	setZoneFilterFromVenueConfig(state, payload) {
		if (payload && payload.copilotConfig) {
			if (typeof payload.copilotConfig.zoneFilter === "boolean") {
				state.config.zoneFilter = payload.copilotConfig.zoneFilter;
			}
			if (typeof payload.copilotConfig.promotionsEnabled === "boolean") {
				state.promotionsEnabled = payload.copilotConfig.promotionsEnabled;
			}
		}
	},
	setIsNotificationCustomerReadyToOrder(state, payload) {
		state.isNotificationCustomerReadyToOrder = payload;
	},
};

const actions = {
	// changeSelectedRole({ commit }, roleId) {
	// 	commit('setSelectedRoleId', roleId);
	// },
	addProductOrder({ commit }, productOrder) {
		commit("setProductOrder", productOrder);
	},

	addReservationUserUid({ commit }, uid) {
		commit("setSelectedReservationUserId", uid);
	},

	async initAvailableRoles({ commit }, { user }) {
		if (user != null && user.copilotRoles != null && user.copilotRoles.length > 0) {
			commit("setAvailableRoles", { availableRoles: user.copilotRoles });

			user.copilotRoles.forEach((role) => {
				if (role.active === 1) {
					commit("setSelectedRoleId", { selectedRoleId: role.id });
				}
			});
		} else {
			try {
				let roleResponse = await copilotRepository.getRolesCopilot();
				commit("setAvailableRoles", { availableRoles: roleResponse.copilot_roles });
			} catch (e) {
				console.log("pues hay excepcion", e);
			}
		}
	},

	async updateReservations({ commit }) {
		try {
			commit("setIsLoadingGetReservations", { isLoadingGetReservations: true });
			const resultGetByDay = await reservationRepository.getReservationsByDay(state.selectedDate);

			if (resultGetByDay === 401) {
				return (window.location.href = origin + "/login");
			}

			if (resultGetByDay === 403) {
				return alert("No estas autorizado para acceder a copilot");
			}

			commit("setReservations", { reservations: resultGetByDay.reservations });
			commit("setServiceEvents", { serviceEvents: resultGetByDay.serviceEvents });
			commit("setIsLoadingGetReservations", { isLoadingGetReservations: false });
		} catch (e) {
			console.error(e);
			commit("setIsLoadingGetReservations", { isLoadingGetReservations: false });
		}
	},
	async updateOrdersAndNotifications({ commit }) {
		try {
			// Load notifications using the new API endpoints
			//const [recentNotifications, ticketRequestNotifications] = await Promise.all([notificationRepository.getRecentNotifications(), notificationRepository.getTicketRequestNotifications()])

			// Assign notifications: ticket-request notifications go to notifications array
			// recent notifications go to assistantNotifications array
			//commit('setNotifications', { notifications: ticketRequestNotifications })
			//commit('setAssistantNotifications', { assistantNotifications: recentNotifications })

			// Also load notifications into Pinia store for gradual migration
			try {
				const notificationStore = useNotificationStore();
				await notificationStore.loadNotifications(state.selectedDate);
				commit("setAssistantNotifications", { assistantNotifications: notificationStore.getHistoryNotifications });
				commit("setNotifications", { notifications: notificationStore.getPendingTicketNotifications });
			} catch (error) {
				console.log("Pinia notification store not available:", error);
			}
		} catch (e) {
			console.log(e);
		}
	},
	async getReservationPaymentInfo(_, data) {
		try {
			const ticketResult = await reservationRepository.getPaymentInfo(data);

			let result = ticketResult.result;
			if (result === 0) {
				return ticketResult.ticketItems;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async getBillPaymentInfo(_, data) {
		try {
			const ticketResult = await reservationRepository.getBillPaymentInfo(data);

			let result = ticketResult.result;
			if (result === 0) {
				return ticketResult;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async checkServerLogicalDate({ commit, state }) {
		try {
			const logicalDate = await configRepository.getLogicalDate();

			// Only update if we got a valid date, otherwise keep the existing value
			if (logicalDate) {
				commit("setServerLogicalDate", logicalDate);
				return logicalDate;
			} else {
				// If API call failed, return the current serverLogicalDate if it exists
				console.warn("[checkServerLogicalDate] Failed to get logical date from API, keeping existing value");
				return state.serverLogicalDate;
			}
		} catch (e) {
			console.error("[checkServerLogicalDate] Error:", e);
			// Return existing value instead of undefined
			return state.serverLogicalDate;
		}
	},
	async getBillPrintInfo(_, data) {
		try {
			const printInfoResult = await reservationRepository.getBillPrintInfo(data);

			let result = printInfoResult.result;
			if (result === 0) {
				return printInfoResult;
			}

			return null;
		} catch (e) {
			console.log(e);
		}
	},
	async loadVenueMenus({ commit }) {
		try {
			commit("setIsLoadingVenueMenus", { isLoadingVenueMenus: true });
			const menus = await menuRepository.getAvailableMenus();
			if (menus.result === 0) {
				commit("setVenueMenus", { venueMenus: menus.menus });
			}
		} catch (error) {
			console.error("Failed to fetch venue menus", error);
		} finally {
			commit("setIsLoadingVenueMenus", { isLoadingVenueMenus: false });
		}
	},
	async getTables({ commit }) {
		try {
			const roomsAndTablesResult = await copilotRepository.getTables();
			let rooms = roomsAndTablesResult.rooms;

			if (rooms.length >= 0) {
				console.log("tables");
			} else {
				rooms = Object.values(rooms);
			}
			const tables = rooms.flatMap((room) =>
				room.tables.map((table) => ({
					...table,
					room: room.name,
				})),
			);
			if (roomsAndTablesResult.result === 0) {
				commit("setTables", { tables: tables });
			}
		} catch (error) {
			console.error("Failed to fetch venue menus", error);
		}
	},

	getIdReservation({ commit }, id) {
		commit("setReservationId", id);
	},

	changeIsRemoveProduct({ commit }, status) {
		commit("setIsRemoveProduct", status);
	},
	saveLocalProductIdModalReject({ commit }, status) {
		commit("setLocalProductIdModalReject", status);
	},
	showToastSuccess({ commit }, payload) {
		commit("setToast", {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: "toast-success",
				type: "success",
			},
		});
	},
	showToastWarning({ commit }, payload) {
		commit("setToast", {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: "toast-warning",
				type: "warning",
			},
		});
	},
	showToastError({ commit }, payload) {
		commit("setToast", {
			toast: {
				toastMessage: payload.message,
				showToast: true,
				idToast: "toast-error",
				type: "error",
			},
		});
	},

	searchAccounts() { },
	setSelectedProductOrderToModifyPrice({ commit }, productOrder) {
		commit("setSelectedProductOrderToModifyPrice", productOrder);
	},
	async updateProductOrderPrice({ commit }, { productOrderId, newPrice }) {
		try {
			// This calls the repository which hits the backend endpoint
			const response = await copilotRepository.updateProductOrderPrice({ productOrderId, newPrice });
			if (response && (response.result === 0 || response.id)) {
				// Success
				commit("showToastSuccess", { message: "Precio actualizado correctamente" });
				return true;
			} else {
				commit("showToastError", { message: "Error al actualizar el precio" });
				return false;
			}
		} catch (error) {
			console.error("Error updating product order price:", error);
			commit("showToastError", { message: "Error al actualizar el precio" });
			return false;
		}
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
